
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Strategy } from "@/types/strategy-types";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, User } from "lucide-react";
import PaymentHeader from "@/components/payment/PaymentHeader";
import PaymentSummary from "@/components/payment/PaymentSummary";
import PaymentMethodSelector from "@/components/payment/PaymentMethodSelector";
import CreditCardForm from "@/components/payment/CreditCardForm";
import PaymentFooter from "@/components/payment/PaymentFooter";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentForm from "@/components/payment/StripePaymentForm";
import VoucherInput from "@/components/payment/VoucherInput";
import { useVoucherCode } from "@/hooks/payment/useVoucherCode";
import { Separator } from "@/components/ui/separator";
import { isIOSDevice } from "@/utils/deviceDetection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface PaymentContainerProps {
  strategy?: Strategy;
  isYearly?: boolean;
  amount: string;
  paymentError: string | null;
  selectedPaymentMethod: string;
  setSelectedPaymentMethod: (method: string) => void;
  isProcessing: boolean;
  stripePromise: any;
  stripePaymentMethods: string[];
  handleStripeSubmit: (paymentMethod: any, promotionCode?: string) => Promise<void>;
  handleManualSubmit: (e: React.FormEvent) => Promise<void>;
}

const PaymentContainer = ({
  strategy,
  isYearly,
  amount,
  paymentError,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  isProcessing,
  stripePromise,
  stripePaymentMethods,
  handleStripeSubmit,
  handleManualSubmit
}: PaymentContainerProps) => {
  const isStripeMethod = stripePaymentMethods.includes(selectedPaymentMethod);
  const {
    voucherCode,
    setVoucherCode,
    validateVoucher,
    clearVoucher,
    isValidatingVoucher,
    validVoucher,
    voucherError
  } = useVoucherCode();

  // Check if the device is running iOS
  const isIOS = isIOSDevice();

  // Calculate discounted amount
  const calculateDiscountedAmount = (): string => {
    if (!validVoucher) return amount;
    
    const originalAmount = parseFloat(amount);
    
    if (validVoucher.discount.type === "percent") {
      const discountPercent = validVoucher.discount.value;
      return (originalAmount * (100 - discountPercent) / 100).toFixed(2);
    } else {
      // Amount discount is stored in cents
      const discountAmount = validVoucher.discount.value / 100;
      return Math.max(0, originalAmount - discountAmount).toFixed(2);
    }
  };

  // Helper function to provide solutions based on error message
  const getErrorSolution = (error: string) => {
    if (error.includes("card was declined") || error.includes("insufficient funds")) {
      return "Try using a different card or payment method.";
    } else if (error.includes("expired")) {
      return "Please update your card information or use a different card.";
    } else if (error.includes("CVC") || error.includes("code")) {
      return "Double-check the security code on the back of your card.";
    } else if (error.includes("connection")) {
      return "Check your internet connection and try again.";
    } else if (error.includes("try again later")) {
      return "Our payment system may be experiencing temporary issues. Please try again in a few minutes.";
    }
    return "If the problem persists, please contact support.";
  };

  // Function to get a user-friendly error message
  const getUserFriendlyErrorMessage = (errorMessage: string) => {
    // List of technical error patterns to filter out
    const technicalErrors = [
      "Edge Function returned a non-2xx status code",
      "network error",
      "internal server error",
      "unexpected error occurred",
      "Something went wrong",
      "Error connecting to payment service"
    ];
    
    // Filter out technical error messages
    let userMessage = errorMessage;
    for (const pattern of technicalErrors) {
      if (userMessage.includes(pattern)) {
        userMessage = userMessage.replace(pattern, "").trim();
        // If we've removed everything, provide a generic message
        if (!userMessage) {
          return "There was a problem processing your payment.";
        }
      }
    }
    
    // Remove any leading/trailing punctuation
    userMessage = userMessage.replace(/^[:.;,\s]+|[:.;,\s]+$/g, "");
    
    return userMessage || "There was a problem processing your payment.";
  };

  // Modified submit handler that includes promo code
  const handleStripeSubmitWithPromo = (paymentMethod: any) => {
    return handleStripeSubmit(paymentMethod, validVoucher?.code);
  };

  // For iOS devices, show a specific message and redirect to account
  if (isIOS) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="p-6">
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold mb-4 text-corporate-primary-100">
                Subscription Management
              </h2>
              <p className="mb-6 text-gray-600 max-w-md mx-auto">
                Due to App Store policies, in-app purchases are not available in the iOS app. 
                Please manage your subscription through your account settings.
              </p>
              <Link to="/account">
                <Button size="lg" className="bg-corporate-primary-100 hover:bg-corporate-primary-75 px-8">
                  Manage Account <User className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="space-y-6"
      >
        <PaymentHeader />

        <Card className="p-6">
          <PaymentSummary 
            strategy={strategy}
            isYearly={isYearly}
            amount={amount}
            discountedAmount={validVoucher ? calculateDiscountedAmount() : undefined}
          />

          {paymentError && (
            <Alert variant="destructive" className="my-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="font-medium">Payment Failed</AlertTitle>
              <AlertDescription className="mt-1">
                <p className="mb-2">{getUserFriendlyErrorMessage(paymentError)}</p>
                <p className="text-sm">{getErrorSolution(paymentError)}</p>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            <PaymentMethodSelector
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
            />

            {/* Add voucher code section above payment forms */}
            <div className="pt-2">
              <VoucherInput
                voucherCode={voucherCode}
                setVoucherCode={setVoucherCode}
                validateVoucher={validateVoucher}
                clearVoucher={clearVoucher}
                isValidatingVoucher={isValidatingVoucher}
                validVoucher={validVoucher}
                voucherError={voucherError}
              />
            </div>

            <Separator className="my-4" />

            {isStripeMethod ? (
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  onSubmit={handleStripeSubmitWithPromo}
                  isProcessing={isProcessing}
                  amount={validVoucher ? calculateDiscountedAmount() : amount}
                  paymentMethod={selectedPaymentMethod}
                />
              </Elements>
            ) : selectedPaymentMethod === "credit-card" ? (
              <CreditCardForm
                onSubmit={handleManualSubmit}
                isProcessing={isProcessing}
                amount={validVoucher ? calculateDiscountedAmount() : amount}
              />
            ) : null}
          </div>

          <PaymentFooter />
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentContainer;
